import { useContext, useEffect, useState } from 'react'

import { type SanityFrameBuilderStrings } from '../../data/sanity/queries/types/strings'
import { type FrameBuilderConfigurationWithLimits } from '../configuration'
import { isSizeWithinBounds } from '../size'
import {
  getIsArtworkImageValid,
  getIsArtworkSizeValid,
  getIsGlassValid,
  getIsPassepartoutColorValid,
  getIsPassepartoutSizeValid,
  getIsProfileValid,
  getIsTextureValid,
} from './state-is-valid'
import { FrameBuilderConfigurationContext } from '../../contexts/configuration-context'
import { FrameBuilderSelectedStateContext } from '../../contexts/state-context'
import { FrameBuilderStringsContext } from '../../contexts/strings-context'

export interface ValidationErrors {
  artowkSizePreview?: string
  frameStyleProfilePreview?: string
  frameStyleTexturePreview?: string
  passepartoutSizePreview?: string
  passepartoutColorPreview?: string
  glassTypePreview?: string
  artworkSize?: string
  artworkImage?: string
}

/**
 * Gets artwork size selection state error message.
 */
const getArtworkSizeSelectionError = (
  configuration: FrameBuilderConfigurationWithLimits,
  frameBuilderStrings: SanityFrameBuilderStrings,
  artworkWidth?: number,
  artworkHeight?: number,
): string | undefined => {
  if (!artworkWidth && !artworkHeight) {
    return frameBuilderStrings.artworkSizeNoSelection
  }

  if (!artworkWidth) {
    return frameBuilderStrings.artworkSizeNoWidthSelected
  }

  if (!artworkHeight) {
    return frameBuilderStrings.artworkSizeNoHeightSelected
  }

  // Check if width or height is out bounds (global profile minimum or maximum)
  const isSizeValid = isSizeWithinBounds(
    [artworkWidth, artworkHeight],
    [configuration.limits.minimumSize, configuration.limits.minimumSize],
    [configuration.limits.maximumWidth, configuration.limits.maximumHeight],
  )

  if (!isSizeValid) {
    return `${frameBuilderStrings.artworkSizeOutOfProfileLimits}`
      .replace('{min}', `${configuration.limits.minimumSize} cm`)
      .replace(
        '{max}',
        `${Math.max(
          configuration.limits.maximumWidth,
          configuration.limits.maximumHeight,
        )} cm`,
      )
      .replace('{maxWidth}', `${configuration.limits.maximumWidth} cm`)
      .replace('{maxHeight}', `${configuration.limits.maximumHeight} cm`)
  }
}

/**
 * Manages step definition validation.
 */
export const useValidationMessages = () => {
  const configuration = useContext(FrameBuilderConfigurationContext)
  const { selectedState } = useContext(FrameBuilderSelectedStateContext)
  const frameBuilderStrings = useContext(FrameBuilderStringsContext)
  const [validationMessages, setValidationMessages] =
    useState<ValidationErrors>({})

  // Validate artwork size step
  useEffect(() => {
    const isArtworkSizeValid = getIsArtworkSizeValid(
      configuration,
      selectedState.artwork.width,
      selectedState.artwork.height,
    )

    setValidationMessages((currentValidationMessages) => ({
      ...currentValidationMessages,
      artowkSizePreview: !isArtworkSizeValid
        ? frameBuilderStrings.artworkSizeSizeNotValid
        : undefined,
      artworkSize: getArtworkSizeSelectionError(
        configuration,
        frameBuilderStrings,
        selectedState.artwork.width,
        selectedState.artwork.height,
      ),
    }))
  }, [
    configuration,
    setValidationMessages,
    selectedState.artwork.height,
    selectedState.artwork.width,
    frameBuilderStrings,
  ])

  // Validate artwork image step
  useEffect(() => {
    const isArtworkImageValid = getIsArtworkImageValid(
      selectedState.artwork.image,
    )

    setValidationMessages((currentValidationMessages) => ({
      ...currentValidationMessages,
      artworkImage: !isArtworkImageValid
        ? frameBuilderStrings.artworkPreviewNoSelection
        : undefined,
    }))
  }, [setValidationMessages, selectedState.artwork.image, frameBuilderStrings])

  // Validate frame style step
  useEffect(() => {
    const isProfileValid = getIsProfileValid(
      selectedState.artwork.width,
      selectedState.artwork.height,
      selectedState.profile,
    )
    const isTextureValid = getIsTextureValid(
      selectedState.profile,
      selectedState.texture,
    )

    setValidationMessages((currentValidationMessages) => ({
      ...currentValidationMessages,
      frameStyleProfilePreview:
        // TODO: selectedState.profile && !isProfileValid
        !isProfileValid
          ? frameBuilderStrings.frameStyleProfileNotValid
          : undefined,
      frameStyleTexturePreview:
        // TODO: selectedState.texture && !isTextureValid
        !isTextureValid
          ? frameBuilderStrings.frameStyleTextureNotValid
          : undefined,
    }))
  }, [
    setValidationMessages,
    selectedState.artwork.height,
    selectedState.artwork.width,
    selectedState.profile,
    selectedState.texture,
    frameBuilderStrings,
  ])

  // Validate passepartout step
  useEffect(() => {
    const isPassepartoutSizeValid = getIsPassepartoutSizeValid(
      selectedState.artwork.width,
      selectedState.artwork.height,
      selectedState.passepartout.size,
      selectedState.passepartout.color,
      selectedState.profile,
    )
    const isPassepartoutColorValid = getIsPassepartoutColorValid(
      selectedState.passepartout.size,
      selectedState.passepartout.color,
      selectedState.profile,
    )

    setValidationMessages((currentValidationMessages) => ({
      ...currentValidationMessages,
      passepartoutSizePreview:
        // TODO: selectedState.passepartout.size && !isPassepartoutSizeValid
        !isPassepartoutSizeValid
          ? frameBuilderStrings.passepartoutSizeNotValid
          : undefined,
      passepartoutColorPreview:
        // TODO: selectedState.passepartout.color && !isPassepartoutColorValid
        !isPassepartoutColorValid
          ? frameBuilderStrings.passepartoutColorNotValid
          : undefined,
    }))
  }, [
    setValidationMessages,
    selectedState.artwork.height,
    selectedState.artwork.width,
    selectedState.passepartout.color,
    selectedState.passepartout.size,
    selectedState.profile,
    frameBuilderStrings,
  ])

  // Validate glass step
  useEffect(() => {
    const isGlassValid = getIsGlassValid(selectedState.glass)

    setValidationMessages((currentValidationMessages) => ({
      ...currentValidationMessages,
      glassTypePreview:
        // TODO: selectedState.glass && !isGlassValid
        !isGlassValid ? frameBuilderStrings.glassTypeNotValid : undefined,
    }))
  }, [setValidationMessages, selectedState.glass, frameBuilderStrings])

  return validationMessages
}
